import type { SiteId } from '@goodbuild/rules/sites';

/**
 * What the boot screen has to say for itself while the session is checked (#135).
 *
 * A games loading screen fills its wait with a tip, and this is that. It is
 * also the one part of the boot screen that carries *content*, which is why it
 * lives here rather than in `apps/app/src/ui` beside `BootScreen`: that package
 * is the brand and the components that carry it, and a sentence about how
 * refunds work is neither. `BootScreen` takes the tip as a prop and has no
 * opinion about where it came from.
 *
 * ## Every line here is a fact, and has to stay one
 *
 * These are the only sentences in the product that a person reads while unable
 * to click anything and check. That makes them the easiest place in the
 * codebase to quietly ship marketing, and the worst one to do it in — a
 * platform whose whole argument is that its numbers carry denominators cannot
 * be padding its loading screen with claims.
 *
 * So each tip below states something the code actually does, and the ones that
 * are close to a rule name it. If a tip and the code ever disagree, the tip is
 * the bug. In particular:
 *
 *   - The refund figures are clause 14.3, and live in
 *     `@goodbuild/rules/refund-policy`. Two hours, fourteen days.
 *   - "Never taken away as a penalty" is a hard rule with no value in
 *     `entitlement_revocation_reason` to express it, by design.
 *   - The creator rate being one rate is #129: `CREATOR_RATE_BPS`, no per-title
 *     column, no negotiated term.
 *   - "Locked at the time of order" is `order_line.creator_rate_bps`, which is
 *     written once and never re-read from the catalogue.
 *   - The processor fee sentence is the merchant-of-record shape: Stripe keeps
 *     the fee on the original payment, so GOODBUILD carries it rather than the
 *     studio.
 *
 * ## Why they are split by surface
 *
 * Somebody booting into a studio portal is not the same reader as somebody
 * booting into the shop, and a tip about basis points is noise to a player the
 * same way a tip about the refund button is noise to a studio. The split is by
 * `SiteId` because that is the answer the boot screen already has — it is being
 * told which colours to wait in, and the audience is the same fact.
 *
 * `EVERYONE` is the tips that are true and interesting on all three, which are
 * mostly the ones about how the product reports on itself. Every surface gets
 * those plus its own, so no pool is thin enough for a wait to loop back round
 * to a tip somebody just read.
 */

/**
 * True on every surface, and worth reading on every surface.
 *
 * These are all about how GOODBUILD reports things, which is the part of the
 * product everybody reading it has a stake in — and the part where we are
 * asking to be trusted, so it is the part worth explaining while somebody is
 * waiting anyway.
 *
 * **Short words.** Every tip is read once, at speed, by somebody who cannot
 * click anything — which makes it the worst place in the product for a long
 * word. `basis points`, `entitlement`, `attribution` and `reversing entry` are
 * all gone, and none of the facts under them moved.
 */
const EVERYONE: readonly string[] = [
  'Every score comes with the number of reviews behind it. “86% positive” is a boast. “86% from 74 reviews on v2.6” is a fact.',
  'The little bars beside a game are its review score, one per version. Make the game better and they climb.',
  'If the bars fall, they fall. A game getting worse is allowed to look like that.',
  /*
    Was: *GOODBUILD publishes what it sells. Studios do not list here, they
    sign…* — which outlived the repositioning at #140 and #141 by describing an
    arrangement that does not exist. Nobody signs to us and no third-party
    studio is coming onto the platform, so the tip was offering a deal on the
    one screen a person cannot click away from.
  */
  'We are a small studio. We make every game here ourselves.',
  'People find our games through creators, not an algorithm. Somebody played it and said so.',
];

/**
 * The pool for each audience, on top of `EVERYONE`.
 *
 * Written for somebody who has just opened that surface: the store's are about
 * buying and owning, the creators' about how a commission is worked out and
 * when the board opens, the studios' about what happens to their money and
 * their builds.
 */
const BY_SPACE: Readonly<Record<SiteId, readonly string[]>> = {
  store: [
    'We never take a game back to punish you. There is no way to even write that down here.',
    'Played under two hours, bought in the last fourteen days? Getting your money back is a button.',
    'No button? That is never a no. You get a person instead. A form does not get to cap a legal right.',
    'A game page is described by the people who played it, not only by the people selling it.',
    'The launcher only fetches the parts you do not already have — from this game, or any other one you own.',
  ],
  creators: [
    'Your rate is the same on every game we make. Nothing to haggle over, and nothing that moves under you.',
    'The rate sticks to a sale the moment it clears. Whatever changes later, that one is already decided.',
    'The board opens once your application is approved. Until then your account page says where it got to.',
    'A refund adds a line to your history instead of changing one. Nothing there is ever rewritten.',
    'Your cut is worked out in whole numbers. The same sale splits the same way every time, anywhere.',
  ],
  /*
    The workshop's ground, which has no screens of its own since #144.

    The five tips here were a studio's: its rates, its refunds, its uploads, its
    store page and the figures in its portal. There are no studios and no
    portal. What is left is true of the workshop generally, and is what somebody
    waiting on this ground is most likely to be doing — pushing a build.
  */
  developers: [
    'gbcli uploads the pieces and publishes a list. The launcher works out what changed and fetches only that.',
    'Every version we ship gets its own bar on the game page, drawn from real review scores or not drawn at all.',
    'There is no running total anywhere in this product. Every figure is added up when it is read.',
    'A game is a draft twice over. Approving it and releasing it are two separate presses.',
  ],
};

/**
 * What to show somebody booting into this surface.
 *
 * A new array each call, which is fine — this is read once per boot screen, and
 * the caller holds the result for as long as the wait lasts. It is not a
 * dependency anything re-renders on.
 */
export function tipsFor(space: SiteId): readonly string[] {
  return [...EVERYONE, ...BY_SPACE[space]];
}
